import { create } from 'zustand';
import { TaskService } from '../services/task.service';
import { CreateTaskData, GroupedTasks, Task, TaskStatus, UpdateTaskData } from '../types/task.types';

interface TaskStore {
  // State
  tasks: GroupedTasks;
  loading: boolean;
  error: string | null;
  
  // Getters
  getPendingTasksCount: () => number;
  
  // Actions
  fetchTasks: () => Promise<void>;
  createTask: (data: CreateTaskData) => Promise<Task>;
  updateTask: (taskId: string, data: UpdateTaskData) => Promise<Task>;
  deleteTask: (taskId: string) => Promise<void>;
  toggleTaskStatus: (taskId: string, currentStatus: TaskStatus) => Promise<void>;
  reorderTask: (taskId: string, newPosition: number) => Promise<void>;
  updatePositions: (status: TaskStatus, taskIds: string[]) => Promise<void>;
  addSubtask: (taskId: string, title: string) => Promise<void>;
  toggleSubtask: (taskId: string, subtaskId: string) => Promise<void>;
  updateSubtask: (taskId: string, subtaskId: string, title: string) => Promise<void>;
  deleteSubtask: (taskId: string, subtaskId: string) => Promise<void>;
  reorderSubtask: (taskId: string, action: ['move-up' | 'move-down', string]) => Promise<void>;
  
  // Internal state updates
  setTasks: (tasks: GroupedTasks) => void;
}

export const useTaskStore = create<TaskStore>((set, get) => ({
  // Initial state
  tasks: { pending: [], completed: [] },
  loading: false,
  error: null,
  
  // Getters
  getPendingTasksCount: () => get().tasks.pending.length,
  
  // Actions
  fetchTasks: async () => {
    try {
      set({ loading: true });
      const groupedTasks = await TaskService.getTasks();
      set({ tasks: groupedTasks, error: null });
    } catch (err) {
      set({ error: 'Error fetching tasks' });
      console.error('Error fetching tasks:', err);
    } finally {
      set({ loading: false });
    }
  },
  
  createTask: async (data: CreateTaskData) => {
    try {
      const taskData = {
        ...data,
        dueDate: data.dueDate ? new Date(data.dueDate) : undefined
      };

      const newTask = await TaskService.createTask(taskData);
      await get().fetchTasks(); // Refresh to get updated positions
      return newTask;
    } catch (err) {
      console.error('Error creating task:', err);
      throw err;
    }
  },
  
  updateTask: async (taskId: string, data: UpdateTaskData) => {
    try {
      const taskData = {
        ...data,
        dueDate: data.dueDate ? new Date(data.dueDate) : undefined
      };

      const updatedTask = await TaskService.updateTask(taskId, taskData);
      await get().fetchTasks(); // Refresh to get updated positions
      return updatedTask;
    } catch (err) {
      console.error('Error updating task:', err);
      throw err;
    }
  },
  
  deleteTask: async (taskId: string) => {
    try {
      await TaskService.deleteTask(taskId);
      await get().fetchTasks();
    } catch (err) {
      console.error('Error deleting task:', err);
      throw err;
    }
  },
  
  toggleTaskStatus: async (taskId: string, currentStatus: TaskStatus) => {
    try {
      const newStatus = currentStatus === TaskStatus.COMPLETED ? TaskStatus.PENDING : TaskStatus.COMPLETED;
      const { tasks } = get();

      // Store original state for rollback
      const originalTasks = { ...tasks };

      // Find and move the task
      const sourceArray = currentStatus === TaskStatus.COMPLETED ? tasks.completed : tasks.pending;
      const destinationArray = currentStatus === TaskStatus.COMPLETED ? tasks.pending : tasks.completed;

      const taskIndex = sourceArray.findIndex(task => task.id === taskId);
      if (taskIndex !== -1) {
        const [task] = sourceArray.splice(taskIndex, 1);
        task.status = newStatus;
        destinationArray.push(task);

        // Update the state immediately
        set({
          tasks: {
            pending: currentStatus === TaskStatus.COMPLETED ? destinationArray : sourceArray,
            completed: currentStatus === TaskStatus.COMPLETED ? sourceArray : destinationArray
          }
        });

        // Make the API call
        try {
          await TaskService.updateTask(taskId, { status: newStatus });
        } catch (error) {
          // Revert changes if the API call fails
          set({ tasks: originalTasks });
          console.error('Error toggling task status:', error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error toggling task status:', error);
      throw error;
    }
  },
  
  reorderTask: async (taskId: string, newPosition: number) => {
    try {
      await TaskService.reorderTask(taskId, newPosition);
      await get().fetchTasks(); // Refresh to get updated positions
    } catch (error) {
      console.error('Error reordering task:', error);
      throw error;
    }
  },
  
  updatePositions: async (status: TaskStatus, taskIds: string[]) => {
    try {
      await TaskService.updatePositions(status, taskIds);
    } catch (error) {
      console.error("Error updating task positions:", error);
      throw error;
    }
  },
  
  addSubtask: async (taskId: string, title: string) => {
    try {
      await TaskService.addSubtask(taskId, title);
      await get().fetchTasks();
    } catch (error) {
      console.error('Error adding subtask:', error);
      throw error;
    }
  },
  
  toggleSubtask: async (taskId: string, subtaskId: string) => {
    try {
      await TaskService.toggleSubtask(taskId, subtaskId);
      await get().fetchTasks();
    } catch (error) {
      console.error('Error toggling subtask:', error);
      throw error;
    }
  },
  
  updateSubtask: async (taskId: string, subtaskId: string, title: string) => {
    const { tasks } = get();
    
    try {
      // Find the task and update the subtask locally first
      const newTasks = { ...tasks };
      for (const status of [TaskStatus.PENDING, TaskStatus.COMPLETED]) {
        const taskIndex = newTasks[status].findIndex(t => t.id === taskId);
        if (taskIndex !== -1) {
          const task = { ...newTasks[status][taskIndex] };
          const subtaskIndex = task.subtasks?.findIndex(st => st.id === subtaskId) ?? -1;
          if (subtaskIndex !== -1 && task.subtasks) {
            task.subtasks = [...task.subtasks];
            task.subtasks[subtaskIndex] = { ...task.subtasks[subtaskIndex], title };
            newTasks[status][taskIndex] = task;
          }
          break;
        }
      }
      
      set({ tasks: newTasks });

      // Update in backend
      await TaskService.updateTask(taskId, {
        subtasks: tasks[TaskStatus.PENDING].find(t => t.id === taskId)?.subtasks ||
          tasks[TaskStatus.COMPLETED].find(t => t.id === taskId)?.subtasks || []
      });
    } catch (error) {
      console.error('Error updating subtask:', error);
      // Refresh tasks to ensure consistency
      await get().fetchTasks();
      throw error;
    }
  },
  
  deleteSubtask: async (taskId: string, subtaskId: string) => {
    const { tasks } = get();
    
    try {
      // Find the task and remove the subtask locally first
      const newTasks = { ...tasks };
      for (const status of [TaskStatus.PENDING, TaskStatus.COMPLETED]) {
        const taskIndex = newTasks[status].findIndex(t => t.id === taskId);
        if (taskIndex !== -1) {
          const task = { ...newTasks[status][taskIndex] };
          task.subtasks = task.subtasks?.filter(st => st.id !== subtaskId) || [];
          newTasks[status][taskIndex] = task;
          break;
        }
      }
      
      set({ tasks: newTasks });

      // Update in backend
      await TaskService.updateTask(taskId, {
        subtasks: tasks[TaskStatus.PENDING].find(t => t.id === taskId)?.subtasks ||
          tasks[TaskStatus.COMPLETED].find(t => t.id === taskId)?.subtasks || []
      });
    } catch (error) {
      console.error('Error deleting subtask:', error);
      // Refresh tasks to ensure consistency
      await get().fetchTasks();
      throw error;
    }
  },
  
  reorderSubtask: async (taskId: string, action: ['move-up' | 'move-down', string]) => {
    const { tasks } = get();
    const [direction, subtaskId] = action;
    
    const task = tasks[TaskStatus.PENDING].find(t => t.id === taskId) ||
      tasks[TaskStatus.COMPLETED].find(t => t.id === taskId);

    if (!task) return;

    const subtaskIndex = task.subtasks.findIndex(s => s.id === subtaskId);
    if (subtaskIndex === -1) return;

    if (direction === 'move-up' && subtaskIndex === 0) return;
    if (direction === 'move-down' && subtaskIndex === task.subtasks.length - 1) return;

    const newSubtasks = [...task.subtasks];
    const targetIndex = direction === 'move-up' ? subtaskIndex - 1 : subtaskIndex + 1;

    [newSubtasks[subtaskIndex], newSubtasks[targetIndex]] =
      [newSubtasks[targetIndex], newSubtasks[subtaskIndex]];

    const newTasks = {
      ...tasks,
      [task.status]: tasks[task.status].map(t =>
        t.id === taskId ? { ...t, subtasks: newSubtasks } : t
      )
    };

    set({ tasks: newTasks });

    try {
      await TaskService.updateTask(taskId, {
        subtasks: newSubtasks
      });
    } catch (error) {
      console.error('Error reordering subtask:', error);
      set({ tasks }); // Revert to original state
      throw error;
    }
  },
  
  // Internal state updates
  setTasks: (tasks: GroupedTasks) => set({ tasks }),
}));
